import { useTranslation } from 'react-i18next';
import { alertDialog } from '@/stores/dialog';

// ==========================================================
// FileBlobDownload — afișează button Descarcă pentru atribute type=file
// după decriptare. Face base64 → Uint8Array → Blob → objectURL → anchor.
// ==========================================================

export function FileBlobDownload({
  base64,
  filename,
}: {
  base64: string;
  filename: string;
}) {
  const { t } = useTranslation();
  const approxBytes = Math.ceil((base64.length * 3) / 4);

  const handleDownload = () => {
    try {
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const mime = guessMime(filename);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      void alertDialog(e instanceof Error ? e.message : t('vault_ui.detail.download_error'));
    }
  };

  return (
    <dd className="flex items-center gap-3 bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-3 py-2 text-[var(--text)]">
      <div className="flex-1 min-w-0 font-mono text-sm truncate">
        {filename}
        <span className="ml-2 text-[11px]" style={{ color: 'var(--text-dim)' }}>
          {formatBytes(approxBytes)}
        </span>
      </div>
      <button
        type="button"
        className="btn-outline text-xs"
        onClick={handleDownload}
      >
        {t('vault_ui.detail.download')}
      </button>
    </dd>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function guessMime(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    zip: 'application/zip',
    gz: 'application/gzip',
    tar: 'application/x-tar',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
  return map[ext] ?? 'application/octet-stream';
}
